import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  AlertCircle,
  Layers,
  RefreshCw,
  Hotel,
  Calendar,
  User,
  Building2,
  Package,
  ChevronDown,
  ChevronUp,
  Radio,
  Loader2,
  Map,
  Filter,
  Wand2
} from 'lucide-react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { LoadingProgress } from '../components/common/LoadingProgress';

// Define Collection type
type Collection = {
  id: string;
  hotels: {
    mainMakkahHotel: string;
    shiftingMakkahHotel?: string;
    madinahHotel: string;
  };
  dateRanges: {
    mainMakkah: { startDate: string; endDate: string };
    shiftingMakkah?: { startDate: string; endDate: string };
    madinah: { startDate: string; endDate: string };
  };
  pilgrims: Array<{
    id: string;
    name: string;
    passportNumber: string;
    gender: string;
    packageName: string;
    guideId: string;
    guideName: string;
    guideOrganizerId?: string;
    guideOrganizerName?: string;
    operatorId?: string;
    operatorName?: string;
    operatorOrganizerId?: string;
    operatorOrganizerName?: string;
  }>;
  warnings: {
    operatorsExceedingLimit: string[];
  };
};

export const OperatorHelperPage = () => {
  const { translations } = useLanguage();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  
  const [isLoading, setIsLoading] = useState(true);
  const [guides, setGuides] = useState<Record<string, any>>({});
  const [packages, setPackages] = useState<Record<string, any>>({});
  const [operationList, setOperationList] = useState<Record<string, any>>({});
  const [collections, setCollections] = useState<Collection[]>([]);
  const [expandedCollections, setExpandedCollections] = useState<Record<string, boolean>>({});
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [pilgrimsPerGuideLimit, setPilgrimsPerGuideLimit] = useState(47);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [organizers, setOrganizers] = useState<Record<string, any>>({});
  const [operatorCollections, setOperatorCollections] = useState<Record<string, string[]>>({});
  const [operatorsInMultipleCollections, setOperatorsInMultipleCollections] = useState<string[]>([]);
  const [selectedOrganizerId, setSelectedOrganizerId] = useState<string>('');
  const [showOrganizerSelector, setShowOrganizerSelector] = useState(false);
  const [operatorDropdowns, setOperatorDropdowns] = useState<Record<string, string>>({});
  const [isSmartAllocating, setIsSmartAllocating] = useState(false);
  const [allocationProgress, setAllocationProgress] = useState({ current: 0, total: 0, step: 1 });
  
  // Fetch necessary data when component mounts
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // For admin, first fetch organizers and show selector
        if (userRole === 'admin') {
          await fetchOrganizers();
          setShowOrganizerSelector(true);
          setIsLoading(false);
          return;
        }
        
        // For other roles, fetch all data
        await fetchAllData();
      } catch (error: any) {
        console.error('Error fetching data:', error);
        setError(error.message || 'An error occurred while loading data');
        setIsLoading(false);
      }
    };
    
    fetchInitialData();
  }, []);
  
  // Fetch all data when organizer is selected (for admin)
  useEffect(() => {
    if (userRole === 'admin' && selectedOrganizerId) {
      fetchAllData();
    }
  }, [selectedOrganizerId]);
  
  // Process collections after data is loaded
  useEffect(() => {
    if (!isLoading && Object.keys(guides).length > 0 && 
        Object.keys(packages).length > 0 && 
        Object.keys(operationList).length > 0) {
      processCollections();
    }
  }, [guides, packages, operationList, isLoading]);

  // Fetch all data
  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      await fetchGuides();
      await fetchOrganizers();
      await fetchPackages();
      await fetchOperationList();
      await fetchSettings();
    } catch (error: any) {
      console.error('Error fetching all data:', error);
      setError(error.message || 'An error occurred while loading data');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch guides for this organizer
  const fetchGuides = async () => {
    try {
      const guidesRef = collection(firestore, 'Guides');
      let guidesQuery;
      
      if (userRole === 'admin' && selectedOrganizerId) {
        guidesQuery = query(guidesRef, where('organizerId', '==', selectedOrganizerId));
      } else if (userRole === 'organizer' && userId) {
        guidesQuery = query(guidesRef, where('organizerId', '==', userId));
      } else if (userRole === 'admin') {
        // If admin but no organizer selected, fetch all guides
        guidesQuery = collection(firestore, 'Guides');
      } else {
        throw new Error('Unauthorized');
      }
      
      const guidesSnapshot = await getDocs(guidesQuery);
      
      if (guidesSnapshot.empty) {
        setGuides({});
        return;
      }
      
      const guidesData: Record<string, any> = {};
      guidesSnapshot.docs.forEach(doc => {
        guidesData[doc.id] = { id: doc.id, ...doc.data() };
      });
      
      setGuides(guidesData);
      setProgress(prev => ({ ...prev, current: 1, total: 4 }));
    } catch (error) {
      console.error('Error fetching guides:', error);
      throw error;
    }
  };

  // Fetch organizers
  const fetchOrganizers = async () => {
    try {
      const organizersRef = collection(firestore, 'Organizers');
      const organizersSnapshot = await getDocs(organizersRef);
      
      if (organizersSnapshot.empty) {
        setOrganizers({});
        return;
      }
      
      const organizersData: Record<string, any> = {};
      organizersSnapshot.docs.forEach(doc => {
        organizersData[doc.id] = { id: doc.id, ...doc.data() };
      });
      
      setOrganizers(organizersData);
      setProgress(prev => ({ ...prev, current: 2, total: 4 }));
    } catch (error) {
      console.error('Error fetching organizers:', error);
      throw error;
    }
  };

  // Fetch packages
  const fetchPackages = async () => {
    try {
      const packagesSnapshot = await getDocs(collection(firestore, 'Packages'));
      
      if (packagesSnapshot.empty) {
        setPackages({});
        return;
      }
      
      const packagesData: Record<string, any> = {};
      packagesSnapshot.docs.forEach(doc => {
        packagesData[doc.data().name] = { id: doc.id, ...doc.data() };
      });
      
      setPackages(packagesData);
      setProgress(prev => ({ ...prev, current: 3, total: 4 }));
    } catch (error) {
      console.error('Error fetching packages:', error);
      throw error;
    }
  };

  // Fetch operation list - only for entries where we are operators
  const fetchOperationList = async () => {
    try {
      const operationRef = collection(firestore, 'operationlist');
      let operationQuery;
      
      if (userRole === 'admin' && selectedOrganizerId) {
        // For admin with selected organizer, get entries where operatorOrganizerId matches
        operationQuery = query(operationRef, where('operatorOrganizerId', '==', selectedOrganizerId));
      } else if (userRole === 'organizer' && userId) {
        // For organizer, get entries where operatorOrganizerId matches
        operationQuery = query(operationRef, where('operatorOrganizerId', '==', userId));
      } else if (userRole === 'guide' && userId) {
        // For guide, get entries where operatorId matches
        operationQuery = query(operationRef, where('operatorId', '==', userId));
      } else if (userRole === 'admin') {
        // If admin but no organizer selected, don't fetch any operations
        setOperationList({});
        return;
      } else {
        throw new Error('Unauthorized');
      }
      
      const operationSnapshot = await getDocs(operationQuery);
      
      if (operationSnapshot.empty) {
        setOperationList({});
        return;
      }
      
      const operationData: Record<string, any> = {};
      operationSnapshot.docs.forEach(doc => {
        // Only include entries where we have valid operator info
        const data = doc.data();
        if (data.operatorId && data.operatorName) {
          operationData[doc.id] = { id: doc.id, ...data };
        }
      });
      
      setOperationList(operationData);
      setProgress(prev => ({ ...prev, current: 4, total: 4 }));
    } catch (error) {
      console.error('Error fetching operation list:', error);
      throw error;
    }
  };

  // Fetch settings for pilgrim per guide limit
  const fetchSettings = async () => {
    try {
      const settingsRef = doc(firestore, 'Settings', 'operatorHelper');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists() && settingsDoc.data().pilgrimsPerGuideLimit) {
        setPilgrimsPerGuideLimit(settingsDoc.data().pilgrimsPerGuideLimit);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Don't throw error, just use default value
    }
  };

  // Process the data into collections
  const processCollections = () => {
    try {
      // Group by package information
      const collectionGroups: Record<string, any[]> = {};
      
      Object.values(operationList).forEach(operation => {
        const packageName = operation.packageName;
        const packageData = packages[packageName];
        
        if (!packageData) {
          // Skip operations with unknown packages
          return;
        }

        // Skip if operator is unknown
        if (!operation.operatorId || !operation.operatorName) {
          return;
        }
        
        // Create a key based on all hotel and date information
        const collectionKey = JSON.stringify({
          mainMakkahHotel: packageData.mainMakkahHotel,
          mainMakkahStartDate: packageData.mainMakkahStartDate,
          mainMakkahEndDate: packageData.mainMakkahEndDate,
          shiftingMakkahHotel: packageData.shiftingMakkahHotel || null,
          shiftingMakkahStartDate: packageData.shiftingMakkahStartDate || null,
          shiftingMakkahEndDate: packageData.shiftingMakkahEndDate || null,
          madinahHotel: packageData.madinahHotel,
          madinahStartDate: packageData.madinahStartDate,
          madinahEndDate: packageData.madinahEndDate
        });
        
        if (!collectionGroups[collectionKey]) {
          collectionGroups[collectionKey] = [];
        }
        
        collectionGroups[collectionKey].push(operation);
      });
      
      // Create collections from groups
      const collectionsArray: Collection[] = [];
      const operatorCollectionsMapping: Record<string, string[]> = {};
      
      Object.entries(collectionGroups).forEach(([key, operations], index) => {
        const packageInfo = JSON.parse(key);
        
        // Skip if no operations
        if (operations.length === 0) {
          return;
        }
        
        // Map operations to pilgrims
        const pilgrims = operations.map(operation => ({
          id: operation.pilgrimId,
          name: operation.pilgrimName,
          passportNumber: operation.passportNumber,
          gender: operation.gender,
          packageName: operation.packageName,
          guideId: operation.guideId,
          guideName: operation.guideName,
          guideOrganizerId: operation.organizerId,
          guideOrganizerName: operation.organizerName,
          operatorId: operation.operatorId,
          operatorName: operation.operatorName,
          operatorOrganizerId: operation.operatorOrganizerId,
          operatorOrganizerName: operation.operatorOrganizerName
        }));
        
        // Track operator collections
        const collectionId = `collection-${index + 1}`;
        pilgrims.forEach(pilgrim => {
          const operatorId = pilgrim.operatorId;
          if (!operatorId) return;
          
          if (!operatorCollectionsMapping[operatorId]) {
            operatorCollectionsMapping[operatorId] = [];
          }
          
          if (!operatorCollectionsMapping[operatorId].includes(collectionId)) {
            operatorCollectionsMapping[operatorId].push(collectionId);
          }
        });
        
        // Count pilgrims per operator
        const pilgrimsByOperator: Record<string, number> = {};
        pilgrims.forEach(pilgrim => {
          if (!pilgrim.operatorId) return;
          pilgrimsByOperator[pilgrim.operatorId] = (pilgrimsByOperator[pilgrim.operatorId] || 0) + 1;
        });
        
        // Find operators exceeding the limit
        const operatorsExceedingLimit: string[] = [];
        Object.entries(pilgrimsByOperator).forEach(([operatorId, count]) => {
          if (count > pilgrimsPerGuideLimit) {
            const operatorName = guides[operatorId]?.name || 'Unknown Operator';
            operatorsExceedingLimit.push(`${operatorName} (${count} pilgrims)`);
          }
        });
        
        // Create collection
        const collection: Collection = {
          id: collectionId,
          hotels: {
            mainMakkahHotel: packageInfo.mainMakkahHotel,
            shiftingMakkahHotel: packageInfo.shiftingMakkahHotel,
            madinahHotel: packageInfo.madinahHotel,
          },
          dateRanges: {
            mainMakkah: {
              startDate: packageInfo.mainMakkahStartDate,
              endDate: packageInfo.mainMakkahEndDate,
            },
            shiftingMakkah: packageInfo.shiftingMakkahHotel ? {
              startDate: packageInfo.shiftingMakkahStartDate,
              endDate: packageInfo.shiftingMakkahEndDate,
            } : undefined,
            madinah: {
              startDate: packageInfo.madinahStartDate,
              endDate: packageInfo.madinahEndDate,
            },
          },
          pilgrims,
          warnings: {
            operatorsExceedingLimit,
          },
        };
        
        collectionsArray.push(collection);
      });
      
      // Find operators in multiple collections
      const multipleColl: string[] = [];
      Object.entries(operatorCollectionsMapping).forEach(([operatorId, collectionIds]) => {
        if (collectionIds.length > 1) {
          multipleColl.push(operatorId);
        }
      });
      
      setOperatorsInMultipleCollections(multipleColl);
      setOperatorCollections(operatorCollectionsMapping);
      
      // Sort collections by pilgrim count (descending)
      collectionsArray.sort((a, b) => b.pilgrims.length - a.pilgrims.length);
      
      // Set state with processed data
      setCollections(collectionsArray);
      
      // Initialize expanded collections state
      // ✅ new code – preserve whatever was already open
      setExpandedCollections(prev => {
        const expanded: Record<string, boolean> = { ...prev };

        // ensure every new/updated collection has a key  
        collectionsArray.forEach(c => {
          if (expanded[c.id] === undefined) expanded[c.id] = false;
        });

        // drop keys for collections that no longer exist  
        Object.keys(expanded).forEach(id => {
          if (!collectionsArray.some(c => c.id === id)) delete expanded[id];
        });

        return expanded;
      });
      
      // Initialize operator dropdown values
      const dropdowns: Record<string, string> = {};
      collectionsArray.forEach(collection => {
        collection.pilgrims.forEach(pilgrim => {
          if (pilgrim.operatorId) {
            dropdowns[pilgrim.id] = pilgrim.operatorId;
          }
        });
      });
      setOperatorDropdowns(dropdowns);
    } catch (error) {
      console.error('Error processing collections:', error);
      setError('Failed to process collections data');
    }
  };

  // Handle Smart Operator Allocation
  const handleSmartOperatorAllocation = async () => {
    if (isSmartAllocating) return;
    setIsSmartAllocating(true);
    
    try {
      // Step 1: Set operator to guide if from same organization
      setAllocationProgress({ current: 0, total: collections.length, step: 1 });
      await performStep1Allocation();
      
      // Step 2: For each collection, find the operator with most pilgrims and assign all to them
      setAllocationProgress({ current: 0, total: collections.length, step: 2 });
      await performStep2Allocation();
      
      // Reload data to reflect changes
      await fetchOperationList();
      processCollections();
      
      toast.success("Smart allocation completed successfully");
    } catch (error: any) {
      console.error('Error during smart allocation:', error);
      toast.error(error.message || 'Failed to perform smart allocation');
    } finally {
      setIsSmartAllocating(false);
    }
  };
  
  // Step 1: Set operator to guide if from same organization
  const performStep1Allocation = async () => {
    let totalProcessed = 0;
    
    for (let collectionIndex = 0; collectionIndex < collections.length; collectionIndex++) {
      const collection = collections[collectionIndex];
      const batch = writeBatch(firestore);
      let batchCount = 0;
      
      // For each pilgrim in this collection
      for (const pilgrim of collection.pilgrims) {
        // Skip if pilgrim has no guide or no guide organizer
        if (!pilgrim.guideId || !pilgrim.guideOrganizerId) continue;
        
        // If guide and operator are from same organization, set operator to guide
        if (pilgrim.operatorOrganizerId === pilgrim.guideOrganizerId) {
          // Find operation in list
          const operationEntryId = Object.entries(operationList).find(
            ([_, entry]: [string, any]) => entry.pilgrimId === pilgrim.id
          )?.[0];
          
          if (operationEntryId) {
            batch.update(doc(firestore, 'operationlist', operationEntryId), {
              operatorId: pilgrim.guideId,
              operatorName: pilgrim.guideName,
              operatorOrganizerId: pilgrim.guideOrganizerId,
              operatorOrganizerName: pilgrim.guideOrganizerName,
              updatedAt: Date.now()
            });
            
            batchCount++;
            
            // Commit batch if it gets too large
            if (batchCount >= 490) {
              await batch.commit();
              batchCount = 0;
            }
          }
        }
      }
      
      // Commit any remaining updates
      if (batchCount > 0) {
        await batch.commit();
      }
      
      totalProcessed++;
      setAllocationProgress(prev => ({ ...prev, current: totalProcessed }));
      
      // Small delay to prevent UI freezing
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  };
  
  // Step 2: For each collection, find operator with most pilgrims and assign all to them
const performStep2Allocation = async () => {
  let totalProcessed = 0;
  const usedOperatorIds = new Set<string>();
  const allOperators = Object.values(guides);

  const currentOrganizerId = userRole === 'admin' ? selectedOrganizerId : userId;

  for (let collectionIndex = 0; collectionIndex < collections.length; collectionIndex++) {
    const collection = collections[collectionIndex];
    const totalPilgrims = collection.pilgrims.length;
    const batches = Math.ceil(totalPilgrims / pilgrimsPerGuideLimit);

    console.log(`📦 Processing collection #${collectionIndex + 1} with ${totalPilgrims} pilgrims`);

    // Step 1: Detect dominant guide (based on guideId) within our organizer
    const guidesById: Record<string, number> = {};
    collection.pilgrims.forEach(p => {
      const guide = guides[p.guideId];
      if (!p.guideId || !guide || guide.organizerId !== currentOrganizerId) return;
      guidesById[p.guideId] = (guidesById[p.guideId] || 0) + 1;
    });

    const sorted = Object.entries(guidesById).sort((a, b) => b[1] - a[1]);
    const dominantGuideId = sorted[0]?.[0];
    const dominantGuide = dominantGuideId ? guides[dominantGuideId] : null;

    console.log('👑 Dominant guide:', dominantGuide?.name || 'None', `(${sorted[0]?.[1] || 0} pilgrims)`);

    // Step 2: Preferred = dominant guide (if available), then others from same organizer
    const preferredOperators: typeof allOperators = [];

    if (dominantGuide && !usedOperatorIds.has(dominantGuide.id)) {
      preferredOperators.push(dominantGuide);
    }

    preferredOperators.push(
      ...allOperators.filter(
        op =>
          op.organizerId === currentOrganizerId &&
          op.id !== dominantGuide?.id &&
          !usedOperatorIds.has(op.id)
      )
    );

    console.log('🎯 Preferred available operators:', preferredOperators.map(op => op.name));

    // Step 3: Assign 47 per operator
    for (let batchIndex = 0; batchIndex < batches; batchIndex++) {
      const start = batchIndex * pilgrimsPerGuideLimit;
      const end = start + pilgrimsPerGuideLimit;
      const pilgrimsSlice = collection.pilgrims.slice(start, end);

      let selectedOperator = preferredOperators.shift();
      let fallbackUsed = '✅ Preferred';

      if (!selectedOperator) {
        selectedOperator = allOperators.find(
          op => op.organizerId === currentOrganizerId && !usedOperatorIds.has(op.id)
        );
        fallbackUsed = '⚠️ Fallback: Any available from organizer';
      }

      if (!selectedOperator && dominantGuide) {
        selectedOperator = dominantGuide;
        fallbackUsed = '🚨 Fallback: Dominant guide reused';
      }

      if (!selectedOperator) {
        console.warn('❌ No operator could be selected for this batch!');
        continue;
      }

      usedOperatorIds.add(selectedOperator.id);
      console.log(`📤 Assigning operator: ${selectedOperator.name} (${fallbackUsed}) for pilgrims ${start + 1} to ${Math.min(end, totalPilgrims)}`);

      const organizerDoc = await getDoc(doc(firestore, 'Organizers', selectedOperator.organizerId));
      const organizerName = organizerDoc.exists() ? organizerDoc.data().name : '';

      const batch = writeBatch(firestore);
      let batchCount = 0;

      for (const pilgrim of pilgrimsSlice) {
        const operationEntryId = Object.entries(operationList).find(
          ([_, entry]: [string, any]) => entry.pilgrimId === pilgrim.id
        )?.[0];

        if (!operationEntryId) continue;

        batch.update(doc(firestore, 'operationlist', operationEntryId), {
          operatorId: selectedOperator.id,
          operatorName: selectedOperator.name,
          operatorOrganizerId: selectedOperator.organizerId,
          operatorOrganizerName: organizerName,
          updatedAt: Date.now()
        });

        batchCount++;

        if (batchCount >= 490) {
          await batch.commit();
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }

      await new Promise(resolve => setTimeout(resolve, 50));
    }

    totalProcessed++;
    setAllocationProgress(prev => ({ ...prev, current: totalProcessed }));
  }

  console.log('✅ Smart allocation completed.');
};





  
  // Calculate statistics for a collection
  const getCollectionStats = (collection: Collection) => {
    // Count total pilgrims
    const totalPilgrims = collection.pilgrims.length;
    
    // Count unique operators
    const uniqueOperators = new Set(collection.pilgrims.map(p => p.operatorId).filter(Boolean)).size;
    
    // Count by operator
    const pilgrimsByOperator: Record<string, number> = {};
    collection.pilgrims.forEach(pilgrim => {
      if (!pilgrim.operatorId) return;
      pilgrimsByOperator[pilgrim.operatorId] = (pilgrimsByOperator[pilgrim.operatorId] || 0) + 1;
    });
    
    // Count by gender
    const maleCount = collection.pilgrims.filter(p => p.gender === 'male').length;
    const femaleCount = collection.pilgrims.filter(p => p.gender === 'female').length;
    
    // Count by operator status
    const operatorPilgrims = collection.pilgrims.filter(p => p.operatorId && p.operatorId !== p.guideId).length;
    const ownOperatorPilgrims = collection.pilgrims.filter(p => !p.operatorId || p.operatorId === p.guideId).length;
    
    return {
      totalPilgrims,
      uniqueOperators,
      pilgrimsByOperator,
      maleCount,
      femaleCount,
      operatorPilgrims,
      ownOperatorPilgrims
    };
  };

  // Check if a collection has warnings
  const hasWarnings = (collection: Collection) => {
    return collection.warnings.operatorsExceedingLimit.length > 0;
  };

  // Handle changing operator for a pilgrim
  const handleChangeOperator = async (pilgrimId: string, newOperatorId: string) => {
    if (isUpdating === pilgrimId) return; // Prevent double submissions
    
    // Update the dropdown value immediately for better UX
    setOperatorDropdowns(prev => ({
      ...prev,
      [pilgrimId]: newOperatorId
    }));
    
    setIsUpdating(pilgrimId);
    try {
      // Find the operator in the guides list
      const operator = guides[newOperatorId];
      if (!operator) {
        throw new Error('Selected operator not found');
      }
      
      // Find the entry in operation list
      const operationEntryId = Object.entries(operationList).find(
        ([_, entry]: [string, any]) => entry.pilgrimId === pilgrimId
      )?.[0];
      
      if (!operationEntryId) {
        throw new Error('Operation entry not found');
      }
      
      // Get organizer data
      let operatorOrganizerId = operator.organizerId;
      let operatorOrganizerName = '';
      
      const organizerRef = doc(firestore, 'Organizers', operatorOrganizerId);
      const organizerDoc = await getDoc(organizerRef);
      if (organizerDoc.exists()) {
        operatorOrganizerName = organizerDoc.data().name;
      }
      
      // Update the operation entry
      const operationRef = doc(firestore, 'operationlist', operationEntryId);
      await updateDoc(operationRef, {
        operatorId: newOperatorId,
        operatorName: operator.name,
        operatorOrganizerId,
        operatorOrganizerName,
        updatedAt: Date.now()
      });
      
      // Update local state
      const updatedOperationList = { ...operationList };
      updatedOperationList[operationEntryId] = {
        ...updatedOperationList[operationEntryId],
        operatorId: newOperatorId,
        operatorName: operator.name,
        operatorOrganizerId,
        operatorOrganizerName
      };
      
      setOperationList(updatedOperationList);
      
      // Update collections
      const updatedCollections = collections.map(collection => {
        const updatedPilgrims = collection.pilgrims.map(pilgrim => {
          if (pilgrim.id === pilgrimId) {
            return {
              ...pilgrim,
              operatorId: newOperatorId,
              operatorName: operator.name,
              operatorOrganizerId,
              operatorOrganizerName
            };
          }
          return pilgrim;
        });
        
        return {
          ...collection,
          pilgrims: updatedPilgrims
        };
      });
      
      setCollections(updatedCollections);
      
      // Re-calculate operator collections
      const updatedOperatorCollections = { ...operatorCollections };
      
      // Remove pilgrim from old operator's collections
      Object.keys(updatedOperatorCollections).forEach(opId => {
        const pilgrimCollections = collections.filter(coll => 
          coll.pilgrims.some(p => p.id === pilgrimId && p.operatorId === opId)
        ).map(coll => coll.id);
        
        if (pilgrimCollections.length > 0) {
          // Remove this operator from collections that no longer have their pilgrims
          updatedOperatorCollections[opId] = updatedOperatorCollections[opId].filter(
            collId => !pilgrimCollections.includes(collId)
          );
        }
      });
      
      // Add pilgrim to new operator's collections
      const newOperatorCollections = collections
        .filter(coll => coll.pilgrims.some(p => p.id === pilgrimId))
        .map(coll => coll.id);
      
      if (!updatedOperatorCollections[newOperatorId]) {
        updatedOperatorCollections[newOperatorId] = [];
      }
      
      newOperatorCollections.forEach(collId => {
        if (!updatedOperatorCollections[newOperatorId].includes(collId)) {
          updatedOperatorCollections[newOperatorId].push(collId);
        }
      });
      
      setOperatorCollections(updatedOperatorCollections);
      
      // Update operators in multiple collections
      const updatedMultipleCollections = Object.entries(updatedOperatorCollections)
        .filter(([_, collIds]) => collIds.length > 1)
        .map(([opId, _]) => opId);
      
      setOperatorsInMultipleCollections(updatedMultipleCollections);
      
      toast.success('Operator updated successfully');
    } catch (error: any) {
      console.error('Error updating operator:', error);
      toast.error(error.message || 'Failed to update operator');
      
      // Revert dropdown value on error
      setOperatorDropdowns(prev => {
        const pilgrim = collections.flatMap(c => c.pilgrims).find(p => p.id === pilgrimId);
        return {
          ...prev,
          [pilgrimId]: pilgrim?.operatorId || ''
        };
      });
    } finally {
      setIsUpdating(null);
    }
  };

  // Filter guides to the same organization as the operator (not the guide)
  const getAvailableOperators = (pilgrim: Collection['pilgrims'][0]) => {
    // Get the operator's organizer
    const operatorOrganizerId = pilgrim.operatorOrganizerId;
    
    if (!operatorOrganizerId) return [];
    
    // Filter guides from the same organization as the operator
    return Object.values(guides).filter(
      guide => guide.organizerId === operatorOrganizerId
    );
  };

  // Toggle collection expansion
  const toggleCollection = (collectionId: string) => {
    setExpandedCollections(prev => ({
      ...prev,
      [collectionId]: !prev[collectionId]
    }));
  };

  // Get collection title
  const getCollectionTitle = (collection: Collection) => {
    let title = `${collection.hotels.mainMakkahHotel}`;
    
    if (collection.hotels.shiftingMakkahHotel) {
      title += ` → ${collection.hotels.shiftingMakkahHotel}`;
    }
    
    title += ` → ${collection.hotels.madinahHotel}`;
    
    return title;
  };

  // Get collection name for display
  const getCollectionName = (collectionId: string) => {
    const collection = collections.find(c => c.id === collectionId);
    if (!collection) return collectionId;
    
    return getCollectionTitle(collection);
  };

  // If loading, show progress
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <LoadingProgress
            progress={progress.current}
            total={progress.total}
            label="Loading data..."
          />
        </div>
        <LoadingSpinner />
      </div>
    );
  }

  // If smart allocating, show progress
  if (isSmartAllocating) {
    return (
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Smart Operator Allocation</h2>
          <div className="space-y-4">
            <div className="bg-primary-50 p-4 rounded-lg">
              <div className="font-medium text-primary-700 mb-2">
                Step {allocationProgress.step}: {allocationProgress.step === 1 ? 
                  'Matching guides and operators from the same organization' : 
                  'Assigning to dominant operators in each collection'}
              </div>
              <LoadingProgress
                progress={allocationProgress.current}
                total={allocationProgress.total}
                label={`Processing collection ${allocationProgress.current} of ${allocationProgress.total}`}
              />
            </div>
            <p className="text-gray-600">Please wait while the smart allocation is being performed...</p>
          </div>
        </div>
        <LoadingSpinner />
      </div>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg">
        <h2 className="text-lg font-semibold mb-2">Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  // For admin, show organizer selector if no organizer is selected
  if (userRole === 'admin' && !selectedOrganizerId) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <Radio className="text-primary-500" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Operator Helper
              </h1>
              <p className="text-gray-500 mt-1">
                Manage operators efficiently based on hotels and date ranges
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={20} className="text-primary-500" />
            <h2 className="text-lg font-semibold">Select an Organizer</h2>
          </div>
          
          <p className="text-gray-600 mb-4">
            Please select an organizer to view their operator data. This helps manage the data more efficiently.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.values(organizers).map(organizer => (
              <motion.div
                key={organizer.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="p-4 border rounded-lg hover:bg-primary-50 hover:border-primary-200 cursor-pointer transition-all"
                onClick={() => setSelectedOrganizerId(organizer.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 rounded-full">
                    <Building2 size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <div className="font-medium">{organizer.name}</div>
                    <div className="text-sm text-gray-500">{organizer.email}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Radio className="text-primary-500" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Operator Helper
            </h1>
            <p className="text-gray-500 mt-1">
              Manage your operators efficiently based on hotels and date ranges
            </p>
          </div>
        </div>
        
        <div className="flex gap-3">
          {userRole === 'admin' && (
            <select
              value={selectedOrganizerId}
              onChange={(e) => setSelectedOrganizerId(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="" disabled>Select Organizer</option>
              {Object.values(organizers).map(organizer => (
                <option key={organizer.id} value={organizer.id}>
                  {organizer.name}
                </option>
              ))}
            </select>
          )}
          
          <button
            onClick={handleSmartOperatorAllocation}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
            disabled={isSmartAllocating || collections.length === 0}
          >
            <Wand2 size={18} />
            <span>Smart Operator Allocation</span>
          </button>
          
          <button 
            onClick={() => {
              setIsLoading(true);
              Promise.all([
                fetchGuides(),
                fetchOrganizers(),
                fetchPackages(),
                fetchOperationList()
              ]).then(() => {
                processCollections();
                setIsLoading(false);
                toast.success('Data refreshed successfully');
              }).catch(err => {
                setIsLoading(false);
                setError(err.message || 'Failed to refresh data');
              });
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
          >
            <RefreshCw size={18} />
            <span>Refresh Data</span>
          </button>
        </div>
      </div>

      {/* Operator Collection Membership Section */}
      {Object.keys(operatorCollections).length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Radio size={20} className="text-primary-500" />
            Operator Collection Membership
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(operatorCollections).map(([operatorId, collectionIds]) => {
              // Skip if operator is not found in guides
              if (!guides[operatorId]) return null;
              
              const operatorName = guides[operatorId]?.name || '';
              const organizerId = guides[operatorId]?.organizerId;
              const organizerName = organizers[organizerId]?.name || '';
              const isInMultipleCollections = operatorsInMultipleCollections.includes(operatorId);
              
              return (
                <div 
                  key={operatorId}
                  className={`p-4 rounded-lg border ${
                    isInMultipleCollections 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <Radio size={16} className="text-indigo-500" />
                        <span className="font-medium">{operatorName}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-1 ml-6 text-xs text-gray-500">
                        <Building2 size={12} />
                        <span>{organizerName}</span>
                      </div>
                    </div>
                    
                    {isInMultipleCollections && (
                      <div className="bg-red-100 text-red-600 px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1">
                        <AlertCircle size={12} />
                        <span>Multiple Collections</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-3 space-y-2">
                    <div className="text-xs font-medium text-gray-500 flex items-center gap-1">
                      <Map size={12} className="text-gray-400" />
                      <span>Collections:</span>
                    </div>
                    
                    {collectionIds.map((collId, idx) => (
                      <div key={idx} className="bg-gray-50 p-2 rounded text-sm">
                        {getCollectionName(collId)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Settings & Current Limits */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users size={20} className="text-primary-500" />
          Settings & Statistics
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-primary-50 rounded-lg">
            <div className="text-primary-700 font-medium">Pilgrims per Operator Limit</div>
            <div className="text-3xl font-bold text-primary-700 mt-2">{pilgrimsPerGuideLimit}</div>
            <div className="text-sm text-primary-600 mt-1">
              Maximum recommended pilgrims per operator
            </div>
          </div>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="text-blue-700 font-medium">Total Collections</div>
            <div className="text-3xl font-bold text-blue-700 mt-2">{collections.length}</div>
            <div className="text-sm text-blue-600 mt-1">
              Groups with the same hotels and dates
            </div>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="text-green-700 font-medium">Total Pilgrims</div>
            <div className="text-3xl font-bold text-green-700 mt-2">
              {collections.reduce((sum, collection) => sum + collection.pilgrims.length, 0)}
            </div>
            <div className="text-sm text-green-600 mt-1">
              Across all collections
            </div>
          </div>
        </div>
      </div>

      {/* Collections */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Layers size={20} className="text-primary-500" />
          Collections ({collections.length})
        </h2>
        
        {collections.map((collection, index) => {
          const stats = getCollectionStats(collection);
          const isExpanded = expandedCollections[collection.id];
          const hasCollectionWarnings = hasWarnings(collection);
          
          // Get operators in multiple collections for this specific collection
          const operatorsWithWarnings = collection.pilgrims
            .filter(p => p.operatorId && operatorsInMultipleCollections.includes(p.operatorId))
            .map(p => p.operatorName);
          
          const uniqueOperatorsWithWarnings = [...new Set(operatorsWithWarnings)];
          
          return (
            <motion.div
              key={collection.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`bg-white rounded-lg shadow-sm border overflow-hidden ${
                hasCollectionWarnings || uniqueOperatorsWithWarnings.length > 0 
                  ? 'border-yellow-300' 
                  : 'border-gray-200'
              }`}
            >
              {/* Collection Header */}
              <div
                className={`p-6 cursor-pointer ${
                  hasCollectionWarnings || uniqueOperatorsWithWarnings.length > 0 
                    ? 'bg-yellow-50' 
                    : ''
                }`}
                onClick={() => toggleCollection(collection.id)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <span className="p-1.5 bg-primary-100 text-primary-700 rounded-lg">
                        {index + 1}
                      </span>
                      {getCollectionTitle(collection)}
                      {(hasCollectionWarnings || uniqueOperatorsWithWarnings.length > 0) && (
                        <AlertCircle size={16} className="text-yellow-500" />
                      )}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      {/* Hotels & Dates */}
                      <div className="space-y-3">
                        <div>
                          <div className="text-sm font-medium text-gray-500 flex items-center gap-1 mb-2">
                            <Hotel size={16} className="text-primary-500" />
                            <span>Hotels and Dates</span>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="bg-gray-50 p-2 rounded-lg">
                              <div className="font-medium text-gray-700 flex items-center gap-1">
                                <span>Main Makkah</span>
                              </div>
                              <div className="text-sm text-gray-600">{collection.hotels.mainMakkahHotel}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                {collection.dateRanges.mainMakkah.startDate} to {collection.dateRanges.mainMakkah.endDate}
                              </div>
                            </div>

                            {collection.hotels.shiftingMakkahHotel && collection.dateRanges.shiftingMakkah && (
                              <div className="bg-gray-50 p-2 rounded-lg">
                                <div className="font-medium text-gray-700 flex items-center gap-1">
                                  <span>Shifting Makkah</span>
                                </div>
                                <div className="text-sm text-gray-600">{collection.hotels.shiftingMakkahHotel}</div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {collection.dateRanges.shiftingMakkah.startDate} to {collection.dateRanges.shiftingMakkah.endDate}
                                </div>
                              </div>
                            )}
                            
                            <div className="bg-gray-50 p-2 rounded-lg">
                              <div className="font-medium text-gray-700 flex items-center gap-1">
                                <span>Madinah</span>
                              </div>
                              <div className="text-sm text-gray-600">{collection.hotels.madinahHotel}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                {collection.dateRanges.madinah.startDate} to {collection.dateRanges.madinah.endDate}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-500 flex items-center gap-1 mb-2">
                          <Users size={16} className="text-primary-500" />
                          <span>Statistics</span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-gray-50 p-2 rounded-lg">
                            <div className="text-xs text-gray-500">Pilgrims</div>
                            <div className="text-lg font-bold text-primary-700">{stats.totalPilgrims}</div>
                          </div>
                          
                          <div className="bg-gray-50 p-2 rounded-lg">
                            <div className="text-xs text-gray-500">Operators</div>
                            <div className="text-lg font-bold text-blue-700">{stats.uniqueOperators}</div>
                          </div>
                          
                          <div className="bg-gray-50 p-2 rounded-lg">
                            <div className="text-xs text-gray-500">Male/Female</div>
                            <div className="text-lg font-bold">
                              <span className="text-blue-700">{stats.maleCount}</span>
                              <span className="text-gray-400 mx-1">/</span>
                              <span className="text-pink-700">{stats.femaleCount}</span>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 p-2 rounded-lg">
                            <div className="text-xs text-gray-500">With Operators</div>
                            <div className="text-lg font-bold text-indigo-700">{stats.operatorPilgrims}</div>
                          </div>
                        </div>
                        
                        {/* Collection Warnings */}
                        {(hasCollectionWarnings || uniqueOperatorsWithWarnings.length > 0) && (
                          <div className="bg-yellow-50 p-2 rounded-lg border border-yellow-200 mt-2">
                            <div className="flex items-center gap-1 mb-1">
                              <AlertCircle size={14} className="text-yellow-600" />
                              <div className="text-xs font-medium text-yellow-700">Warnings</div>
                            </div>
                            
                            <ul className="list-disc list-inside text-xs text-yellow-700 space-y-1">
                              {collection.warnings.operatorsExceedingLimit.map((warning, i) => (
                                <li key={`limit-${i}`}>{warning}</li>
                              ))}
                              
                              {uniqueOperatorsWithWarnings.map((operatorName, i) => (
                                <li key={`multiColl-${i}`}>
                                  Operator <strong>{operatorName}</strong> is assigned to multiple collections
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expand/Collapse Icon */}
                  <div className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                    {isExpanded ? (
                      <ChevronUp size={24} className="text-gray-500" />
                    ) : (
                      <ChevronDown size={24} className="text-gray-500" />
                    )}
                  </div>
                </div>
              </div>
              
              {/* Expanded Content - Pilgrims Table */}
              {isExpanded && (
                <div className="border-t border-gray-200">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Pilgrim
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Package
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Guide
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Operator
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {collection.pilgrims.map((pilgrim) => {
                          // Skip pilgrims with unknown operators or guides
                          if (!pilgrim.operatorId || !guides[pilgrim.operatorId]) return null;
                          
                          // Get operators from the same organizer as the current operator
                          const availableOperators = getAvailableOperators(pilgrim);
                          const isSameOperator = pilgrim.operatorId === pilgrim.guideId;
                          const operatorInMultipleCollections = operatorsInMultipleCollections.includes(pilgrim.operatorId);
                          
                          return (
                            <tr key={pilgrim.id} className="hover:bg-gray-50">
                              {/* Pilgrim Info */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className={`flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full ${
                                    pilgrim.gender === 'male' 
                                      ? 'bg-blue-50 text-blue-500'
                                      : 'bg-pink-50 text-pink-500'
                                  }`}>
                                    <User size={20} />
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">{pilgrim.name}</div>
                                    <div className="text-sm text-gray-500">{pilgrim.passportNumber}</div>
                                  </div>
                                </div>
                              </td>
                              
                              {/* Package */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <Package size={16} className="text-primary-500 mr-2" />
                                  <span className="text-sm text-gray-900">{pilgrim.packageName}</span>
                                </div>
                              </td>
                              
                              {/* Guide */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex flex-col">
                                  <div className="flex items-center">
                                    <User size={16} className="text-blue-500 mr-2" />
                                    <span className="text-sm text-gray-900">{pilgrim.guideName}</span>
                                  </div>
                                  <div className="flex items-center mt-1">
                                    <Building2 size={14} className="text-gray-400 mr-1" />
                                    <span className="text-xs text-gray-500">{pilgrim.guideOrganizerName}</span>
                                  </div>
                                </div>
                              </td>
                              
                              {/* Operator */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex flex-col">
                                  <div className="flex items-center">
                                    <Radio size={16} className={`${operatorInMultipleCollections ? "text-red-500" : "text-indigo-500"} mr-2`} />
                                    <span className="text-sm text-gray-900">
                                      {pilgrim.operatorName}
                                    </span>
                                    {operatorInMultipleCollections && (
                                      <span className="ml-2 text-xs px-1 py-0.5 bg-red-100 text-red-600 rounded">
                                        Multiple Collections
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center mt-1">
                                    <Building2 size={14} className="text-gray-400 mr-1" />
                                    <span className="text-xs text-gray-500">{pilgrim.operatorOrganizerName}</span>
                                  </div>
                                </div>
                              </td>
                              
                              {/* Actions */}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {isUpdating === pilgrim.id ? (
                                  <div className="flex items-center">
                                    <Loader2 size={16} className="text-primary-500 animate-spin mr-2" />
                                    <span>Updating...</span>
                                  </div>
                                ) : (
                                  <select
                                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    value={operatorDropdowns[pilgrim.id] || pilgrim.operatorId || ''}
                                    onChange={(e) => handleChangeOperator(pilgrim.id, e.target.value)}
                                  >
                                    <option value="" disabled>Select operator</option>
                                    {availableOperators.map(operator => (
                                      <option key={operator.id} value={operator.id}>
                                        {operator.name}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Operator Distribution */}
                  <div className="p-6 bg-gray-50 border-t">
                    <h4 className="font-medium mb-4 text-gray-700">Operator Distribution</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(stats.pilgrimsByOperator).map(([operatorId, count]) => {
                        // Skip if operator is unknown
                        if (!guides[operatorId]) return null;
                        
                        const operatorName = guides[operatorId]?.name;
                        const operatorOrganizerId = guides[operatorId]?.organizerId;
                        const organizerName = organizers[operatorOrganizerId]?.name || '';
                        const exceedsLimit = count > pilgrimsPerGuideLimit;
                        const isInMultipleCollections = operatorsInMultipleCollections.includes(operatorId);
                        
                        return (
                          <div 
                            key={operatorId} 
                            className={`p-4 rounded-lg border ${
                              exceedsLimit || isInMultipleCollections 
                                ? 'border-yellow-300 bg-yellow-50' 
                                : 'border-gray-200 bg-white'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <Radio size={16} className={
                                  isInMultipleCollections 
                                    ? "text-red-600" 
                                    : exceedsLimit 
                                      ? "text-yellow-600" 
                                      : "text-indigo-500"
                                } />
                                <span className="font-medium text-gray-800">{operatorName}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`font-bold ${
                                  isInMultipleCollections 
                                    ? "text-red-600" 
                                    : exceedsLimit 
                                      ? "text-yellow-600" 
                                      : "text-indigo-600"
                                }`}>
                                  {count}
                                </span>
                                <span className="text-xs text-gray-500">pilgrims</span>
                              </div>
                            </div>
                            
                            {/* Organizer Name */}
                            {organizerName && (
                              <div className="ml-6 mb-2 flex items-center text-xs text-gray-500">
                                <Building2 size={12} className="mr-1" />
                                <span>{organizerName}</span>
                              </div>
                            )}
                            
                            {/* Warnings */}
                            {isInMultipleCollections && (
                              <div className="ml-6 mb-2 flex items-center text-xs text-red-500">
                                <AlertCircle size={12} className="mr-1" />
                                <span>In multiple collections</span>
                              </div>
                            )}
                            
                            {/* Progress bar */}
                            <div className="mt-2">
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div
                                  className={`h-1.5 rounded-full ${
                                    isInMultipleCollections 
                                      ? 'bg-red-500' 
                                      : exceedsLimit 
                                        ? 'bg-yellow-500' 
                                        : 'bg-indigo-500'
                                  }`}
                                  style={{ width: `${Math.min((count / pilgrimsPerGuideLimit) * 100, 100)}%` }}
                                >
                                </div>
                              </div>
                              <div className="mt-1 flex justify-between text-xs text-gray-500">
                                <span>{Math.round((count / pilgrimsPerGuideLimit) * 100)}% of limit</span>
                                <span>{count}/{pilgrimsPerGuideLimit}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
        
        {collections.length === 0 && (
          <div className="bg-white p-8 rounded-lg shadow-sm text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-gray-100 rounded-full">
                <Layers size={32} className="text-gray-400" />
              </div>
            </div>
            <h3 className="text-xl font-medium text-gray-700">No Collections Found</h3>
            <p className="text-gray-500 mt-2">
              No operation data was found that can be organized into collections.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OperatorHelperPage;
